"use client"

import { Header } from "@/components/header"
import { PromptCard } from "@/components/prompt-card"
import { Button } from "@/components/ui/button"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { homePromptIds } from "@/lib/mock-data"
import type { PromptSummary } from "@/lib/mock-data"
import { useAppState } from "@/lib/app-state"
import { dictionary, getLocaleFromPathname, hrefWithLocale } from "@/lib/i18n"

type Props = {
  popularPrompts: PromptSummary[]
  homeError?: string
}

export default function HomePageClient({ popularPrompts, homeError }: Props) {
  const pathname = usePathname()
  const locale = getLocaleFromPathname(pathname)
  const t = dictionary[locale].home
  const { favorites, addToCart, toggleFavorite } = useAppState()

  const missingHomeIds = homePromptIds.filter((id) => !popularPrompts.some((p) => p.id === id))

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />

      {homeError ? (
        <div className="border-b border-destructive/50 bg-destructive/10 px-4 py-3 text-center text-sm text-destructive">
          인기 프롬프트를 불러오지 못했습니다: {homeError}
        </div>
      ) : null}

      {missingHomeIds.length > 0 && !homeError ? (
        <div className="border-b border-muted bg-muted/30 px-4 py-2 text-center text-xs text-muted-foreground">
          일부 노출 예정 프롬프트는 아직 게시되지 않아 목록에서 제외되었습니다.
        </div>
      ) : null}

      <section className="py-20 text-center">
        <div className="container mx-auto px-4">
          <h1 className="text-5xl font-bold mb-6 text-balance">
            {t.heroTitleTop}
            <br />
            {t.heroTitleBottom}
          </h1>
          <p className="mx-auto mb-12 max-w-2xl text-xl text-muted-foreground text-pretty">{t.heroDescription}</p>
          <div className="flex justify-center space-x-4">
            <Button size="lg" className="px-8" asChild>
              <Link href={hrefWithLocale(locale, "/prompts")}>{t.browsePrompts}</Link>
            </Button>
            <Button variant="outline" size="lg" className="bg-transparent px-8" asChild>
              <Link href={hrefWithLocale(locale, "/waitlist")}>{t.becomeSeller}</Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center mb-12">
            <h2 className="text-3xl font-bold">{t.popularPrompts}</h2>
            <Button variant="ghost" className="text-muted-foreground hover:text-foreground" asChild>
              <Link href={hrefWithLocale(locale, "/prompts")}>{t.viewAll}</Link>
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {popularPrompts.map((prompt) => (
              <PromptCard
                key={prompt.id}
                {...prompt}
                isFavorited={favorites.includes(prompt.id)}
                onAddToCart={addToCart}
                onToggleFavorite={toggleFavorite}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="border-t py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold mb-2">10,000+</div>
              <div className="text-muted-foreground">{t.statPrompts}</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">5,000+</div>
              <div className="text-muted-foreground">{t.statActiveUsers}</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">98%</div>
              <div className="text-muted-foreground">{t.statSatisfaction}</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">24/7</div>
              <div className="text-muted-foreground">{t.statSupport}</div>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h3 className="font-bold text-lg mb-4">PromptMarket</h3>
              <p className="text-muted-foreground">{t.footerTagline}</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">{t.footerService}</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li>
                  <a href="#" className="hover:text-foreground">
                    {t.footerBuyPrompts}
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-foreground">
                    {t.footerSellPrompts}
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-foreground">
                    {t.footerPremium}
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">{t.footerSupport}</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li>
                  <a href="#" className="hover:text-foreground">
                    {t.footerHelp}
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-foreground">
                    {t.footerContact}
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-foreground">
                    FAQ
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">{t.footerCompany}</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li>
                  <a href="#" className="hover:text-foreground">
                    {t.footerTerms}
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-foreground">
                    {t.footerPrivacy}
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="mt-12 border-t pt-8 text-center text-muted-foreground">
            <p>© 2025 PromptMarket. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  )
}
